<template>
  <p
    ref="dateRef"
    v-tooltip="{
      content: createdDate,
      onShow: () => (isFixed ? isFixedTextTruncated : isDateTruncated),
    }"
    data-cy="time-ago"
    class="cursor-pointer text-xs text-label"
    @click="handleClick"
  >
    {{ createdDate }}
  </p>
</template>
<script setup lang="ts">
import { nextTick } from "vue";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { isToday } from "date-fns/isToday";

import { AnalyticsEvent } from "@/enums/analytics";
import { formatDateCustom, formatTimeAgo } from "@/helpers/formatting";
import { useElementStatus } from "@/hooks/elements";
import { useAnalyticsStore } from "@/stores/analytics";

const {
  date = "",
  showTimeIfToday = false,
  isFixed = false,
  format = "MMMM dd, yyyy hh:mm a",
} = defineProps<{
  date?: string;
  showTimeIfToday?: boolean;
  isFixed?: boolean;
  format?: string;
}>();

const { trackEvent } = useAnalyticsStore();
const { isTextTruncated } = useElementStatus();
const { t } = useI18n();

const showFullDateAndTime = ref(isFixed);
const dateRef = ref<HTMLElement | null>(null);
const isDateTruncated = ref(isTextTruncated(dateRef).value);

const isFixedTextTruncated = isTextTruncated(dateRef);

const createdDate = computed(() => {
  if (!date) {
    return "";
  }

  if (isFixed) {
    return isToday(new Date(date))
      ? t("EMAIL_INTEGRATION.TODAY_AT_TIME", {
          time: formatTimeAgo(date, showTimeIfToday),
        })
      : formatDateCustom(date, format, true);
  }

  return showFullDateAndTime.value
    ? formatDateCustom(date, format, true)
    : formatTimeAgo(date, showTimeIfToday);
});

const handleClick = () => {
  if (isFixed) {
    return;
  }

  showFullDateAndTime.value = !showFullDateAndTime.value;
  trackEvent(AnalyticsEvent.TimeAgoToFullTimeUsed, {
    date,
    isShowingFullTime: showFullDateAndTime.value,
  });
  nextTick(() => {
    isDateTruncated.value = isTextTruncated(dateRef).value;
  });
};
</script>
